import React, { useRef, useState } from 'react';
import { Search } from '@americanexpress/dls-react';
import { compose } from 'redux';
import { useSelector } from 'react-redux';
import { withRouter } from '@americanexpress/one-app-router';
import PropTypes from 'prop-types';
import { getLocaleData } from '@/selectors/localeSelectors';
import { useTranslatedAirportResults } from '@/hooks/airportHooks/airportsHelperHooks';
import {
  useCommonContent,
  useOneCmsLocaleMinimumSearchLength,
  useOneCmsLocaleSearchObj,
} from '@/hooks/oneCmsIntlHooks/oneCmsIntlHelperHooks';
import { useAnalyticsContext } from '@/context/analyticsContext';
import { useRouteParams } from '@/context/routeParamsContext';
import { cn } from '@/helpers/utils';
import styles from './header/GlobalHeader.module.scss';
import AirportLinkWithImage from './AirportLinkWithImage';
import LFLink from './LFLink';
import ChevronLeft from '../icons/ChevronLeft';

const TCNSearchBox = ({ toggleFindAirportMenu }) => {
  const [query, setQuery] = useState('');
  const [routedAirport] = useState('');
  const { product } = useRouteParams();
  const { locale } = useSelector(getLocaleData);
  const isUsMarket = !locale || locale.toLocaleLowerCase() === 'en-us';
  const { featuredAirports, header } = useCommonContent();
  const searchCopy = useOneCmsLocaleSearchObj();

  const { customTrack } = useAnalyticsContext();

  const inputRef = useRef(null);
  const airportsMatchingQuery = useTranslatedAirportResults(query);
  const minQueryLength = useOneCmsLocaleMinimumSearchLength();

  const onFocus = () => {
    if (query === routedAirport) {
      setQuery('');
    }
  };

  const onBlur = (e) => {
    if (query === '') {
      setQuery(routedAirport);
    }
    customTrack({ event: e, detail: query });
  };

  const handleOnChange = (event) => {
    setQuery(event.target.value);
  };

  const hasSearchQuery = query.length >= minQueryLength;
  const hasResults = airportsMatchingQuery.length > 0;
  const noResults = hasSearchQuery && !hasResults;

  const airportsList = hasSearchQuery
    ? airportsMatchingQuery
    : featuredAirports.airports;

  let resultsHeading = featuredAirports.header;

  if (hasSearchQuery) {
    resultsHeading = hasResults
      ? `Results for "${query}"`
      : `No Results for "${query}"`;
  }

  return (
    <div className="tw-mx-auto tw-w-full tw-px-4 md_tw-px-6 lg_tw-max-w-360 lg_tw-px-8 xl_tw-px-10">
      <div className="tw-flex tw-flex-col tw-items-start tw-gap-6 tw-py-6 md_tw-flex-row md_tw-justify-start md_tw-gap-6 lg_tw-gap-10">
        <div
          className={cn(styles.searchBar, 'tw-w-full md_tw-mb-0 md_tw-w-80')}
        >
          <h1 className="tw-heading-xs-bold tw-pb-4 tw-font-bold tw-text-gray-300">
            {header.searchTabTitle}
          </h1>
          <Search
            id="findLounges"
            onBlur={onBlur}
            onFocus={onFocus}
            onClearSearch={() => setQuery('')}
            value={query || ''}
            containerRef={inputRef}
            onChange={handleOnChange}
            isOpen={false}
            placeholder={searchCopy.placeholder}
          />
        </div>
        <div className="tw-hidden tw-w-px tw-self-stretch tw-bg-blue-deep-tint-700-t12 md_tw-flex" />
        <div className="tw-w-full tw-min-w-0">
          <h1 className="tw-heading-xs-bold tw-mb-4 tw-font-bold tw-text-gray-300">
            {resultsHeading}
          </h1>
          <ul className="scrollbar-hide tw-grid tw-snap-x tw-snap-mandatory tw-auto-cols-max tw-grid-flow-col tw-grid-rows-2 tw-gap-x-6 tw-gap-y-6 tw-overflow-x-auto tw-overflow-y-hidden tw-overscroll-x-contain tw-scroll-smooth lg_tw-snap-none lg_tw-auto-cols-auto lg_tw-grid-flow-row lg_tw-grid-cols-3 lg_tw-grid-rows-2 lg_tw-overflow-visible">
            {airportsList.map((airport) => {
              return (
                <li
                  key={airport.iata}
                  className="tw-mt-2 tw-w-72 tw-shrink-0 tw-snap-start lg_tw-w-auto lg_tw-snap-none"
                >
                  <AirportLinkWithImage
                    airport={airport}
                    background="dark"
                    onClickHandler={toggleFindAirportMenu}
                  />
                </li>
              );
            })}
          </ul>
          {isUsMarket && (
            <div>
              <LFLink
                to={`${product}/all-airports/`}
                className={cn(
                  'tw-inline-flex tw-items-center tw-font-sans tw-text-base tw-font-medium tw-text-blue-bright-400',
                  { 'tw-pt-4': !noResults }
                )}
                element="all-airports-link"
                onClickHandler={toggleFindAirportMenu}
              >
                {featuredAirports?.link}
                <ChevronLeft className="tw-blue-bright-400 tw-ml-1 tw-inline tw-h-3 tw-w-3 tw-rotate-180" />
              </LFLink>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};
const hocChain = compose(withRouter);

TCNSearchBox.propTypes = {
  toggleFindAirportMenu: PropTypes.func,
};

export default hocChain(TCNSearchBox);
